import { PolygonROI } from './polygon-roi';
import { ROICorners, RectangleROI } from './rectangle-roi';
import { FeatureMode } from './feature-mode';
import { SelectionProxy } from './../../model/selection-proxy';
import { SnapSegs } from './../../model/drawing/SnapSegs';
import { SnapPts } from './../../model/drawing/SnapPts';
import { Component, OnInit, ViewChild, ElementRef, ViewEncapsulation, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import * as d3 from 'd3';
import { IFeatureContext, IFeature, Feature } from 'src/app/model/drawing/Feature';
import { PolyFeature } from 'src/app/model/drawing/PolyFeature';
import { LineFeature } from 'src/app/model/drawing/LineFeature';
import { PointFeature } from 'src/app/model/drawing/PointFeature';
import { PixoSpatial, PixoSpatialCoord, SpatialCoord, PixoPt } from 'src/app/model/Pixospatial';
import { SLLayer, LayerGeomtypes } from 'src/app/model/sl-layer';
import { ModalMessagerService } from 'src/app/modal-messager/modal-messager.service';
import { FeatureService } from 'src/app/model/api-services/feature.service';
import { SubScriptionMgr } from 'src/app/model/SubscriptionMgr';
import { Dict } from 'src/app/types/decorable';
import { GeoJSONObject, GeometryObject, segmentEach } from '@turf/turf';
import { AnyHandler, SimpleAVHandler, SimpleHandler, SimpleRecord } from 'src/app/types/@types/sl-v-types';
import { StatefulCSS } from 'src/app/model/stateful_css';
import { ISLMap } from 'src/app/types/@types/sl-map';
import { IDrawContext, IMapInteraction } from 'src/app/components/draw-pane/draw-pane-utils'
import { CancellablePromise } from 'src/app/types/cancellable-promise';
import { LngLatBounds } from 'mapbox-gl';
import { EditorAction } from 'src/app/model/map-actions/editor-action';
import { SLMap } from 'src/app/model/sl-map';
import { SLViewerComponent } from 'src/app/mapps/sl-viewer/sl-viewer.component';
import { DisplayAction } from 'src/app/model/map-actions/display-actions';
export interface FeatureInfo {
  layer: SLLayer;
  gid: number | string; // feature id
}

export interface ROIEvent {
  type: 'mouse' | 'touch';
  roiElement: SVGElement;
  location: PixoSpatialCoord | null;
  location2?: PixoSpatialCoord | null;
  action: 'clear' | 'start' | 'move' | 'end';
  left: boolean;
  right: boolean;
  middle: boolean;
}
export type SnapGeomMode = 'any' | 'point' | 'segment';
export type DrawMode = '' | 'feature' | 'roi' | 'roi-repeating' | 'roi-polygon' | 'distance' | 'snap' | 'roi-snap';
export type DrawInteractionModes = '' | 'feature' | 'rectangle' | 'polygon';
@Component({
  selector: 'app-draw-pane',
  templateUrl: './draw-pane.component.html',
  styleUrls: ['./draw-pane.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DrawPaneComponent implements OnInit, OnDestroy {

  @ViewChild('svg', { static: true }) svg: ElementRef<SVGElement>;

  private geomType: string = null;
  private gid: string = null;
  private defaultState = 'feature';

  @Input('type')
  set type(newType: string) {
    if (newType === this.geomType) return;
    this.geomType = newType;
    this.Clear().NewFeature();
  }

  get type(): string {
    return this.geomType;
  }
  set domElement(e: HTMLElement) {
    // do nothing
  }

  @Output()
  get domElement(): HTMLElement {
    return this.domNode.nativeElement;
  }

  @Input()
  public map: ISLMap;

  @Input() width: any = '100%';
  @Input() height: any = '100%';
  public spatialContext: PixoSpatial = null;

  @Output() roi = new EventEmitter<ROIEvent>();

  @Output() typeChange = new EventEmitter<string>();
  get hasVertices() {
    if (!this.GetSelectedFeature()) {
      return false;
    }

    return this.GetSelectedFeature().hasVertices;

  }
  @Output() hasVerticesChange = new EventEmitter<boolean>();

  @Input() isOn = false;
  @Output() isOnChange = new EventEmitter<boolean>();

  @Input() isROIOn = false;

  @Input() isDistanceOn = false;

  @Output()
  geometryChange = new EventEmitter<Dict>();

  @Output()
  selectionChange = new EventEmitter<Dict>();

  public layer: SLLayer;
  private d3svg: any;
  private features: Array<any> = [];
  private selectedFeature: Feature = null;
  private started = false;
  private newFeatureOk = false;
  private _drawMode: DrawMode = 'feature';
  private _xactionMode: DrawInteractionModes = '';
  public set drawMode(mode: DrawMode) {
    this._drawMode = mode;
    switch (this._drawMode) {
      case 'roi-snap':
        this.SetInteractionMode('polygon');
        break;
      case 'roi-repeating':
        this.SetInteractionMode('rectangle', undefined, { autoEnd: false })
        break;
      case 'roi':
        this.SetInteractionMode('rectangle');
        break;
      case 'roi-polygon':
        this.SetInteractionMode('polygon');
        break;
      case 'feature':
        this.SetInteractionMode('feature');
        break;
      case '':
        this.SetInteractionMode('');
      case 'distance':
      case 'snap':
        this.SetInteractionMode('');
        break;
    }
    this.modeStates.SetState(mode);
  }
  public get drawMode(): DrawMode {
    return this.modeStates.GetCurrentState() as DrawMode;
  }
  private interactionModes: SimpleRecord<DrawInteractionModes, IMapInteraction> = {}
  public WindowUpHandler: (event: MouseEvent) => void;
  private drawModes = ['feature'];
  public drawWidth = 0;
  public drawHeight = 0;
  private ROIElement;
  private handledMouse = false;
  private handledTouch = false;
  private drawingOn = false;
  private snapPts: SnapPts;
  private snapSegs: SnapSegs;
  private stateMgr = new StatefulCSS();
  private modeStates = new StatefulCSS();
  public featureContext: IFeatureContext = null;
  protected subscriptions: SubScriptionMgr = new SubScriptionMgr();

  public selectionProxy: SelectionProxy = new SelectionProxy();

  get isSnapOn() {
    return this.modeStates.GetCurrentState() === 'snap';
  }
  get isSnapROIOn() {
    return this.modeStates.GetCurrentState() === 'snap-roi';
  }
  get isSnapROIPolyOn() {
    return this.modeStates.GetCurrentState() === 'snap-roi-polygon';
  }

  get container() {

    return this.d3svg;
  }
  get interactionMode(): DrawInteractionModes {
    return this._xactionMode;
  }
  public snapGroupMode: boolean = false;
  private _snapGeomMode: SnapGeomMode = 'any';
  public set snapGeomMode(mode: SnapGeomMode) {
    this._snapGeomMode = mode;
    this.selectionProxy.ToggleUpdating(true);
    switch (mode) {
      case 'point':
        this.snapPts?.SetActive(true);
        this.snapSegs?.SetActive(false);
        break;
      case 'segment':
        this.snapPts?.SetActive(false);
        this.snapSegs?.SetActive(true);
        break;
      case 'any':
      default:
        this.snapPts?.SetActive(true);
        this.snapSegs?.SetActive(true);
        break;
    }
    this.selectionProxy.ToggleUpdating();
  }
  public get snapGeomMode(): SnapGeomMode {
    return this._snapGeomMode;
  }

  constructor(
    private domNode: ElementRef,
    private messager: ModalMessagerService,
    private featureSvc: FeatureService
  ) { }

  ngOnInit() {
    this.stateMgr.SetMachine(this.domElement);
    this.stateMgr.SetPrefix('drawing');
    this.stateMgr.AddState('on');
    this.stateMgr.AddState('off');
    this.modeStates.SetMachine(this.domElement);
    this.modeStates.SetPrefix('draw-mode');
    this.modeStates.AddState('feature');
    this.modeStates.AddState('roi');
    this.modeStates.AddState('roi-polygon');
    this.modeStates.AddState('distance');
    this.modeStates.AddState('snap');
    this.modeStates.AddState('roi-snap');

    this.modeStates.SetState(this._drawMode);
    this.selectionProxy.AddProperty('vertex');
    this.selectionProxy.AddProperty('segment');
    this.selectionProxy.AddProperty('snapVertex');
    this.selectionProxy.AddProperty('snapSegment');
    this.width = this.domNode.nativeElement.clientWidth;
    this.height = this.domNode.nativeElement.clientHeight;
    this.UpdateSize(this.width, this.height);

    this.SetupSVGElement();
    this.SetupFeatureContexts()

    if (this.isOn) {
      this.TurnOn();
    } else {
      this.TurnOff();
    }





    this.SetupInteractionModes();


    this.started = true;
    this.SetInteractionMode('feature');
    if (this.geomType !== null) {
      if (this.features.length === 0) {
        this.NewFeature();
      }
    }

    this.SetupSnaps();
  }

  SetupFeatureContexts() {
    this.featureContext = {
      updateHandler: (feature: Feature) => {
        this.hasVerticesChange.emit(feature.hasVertices);
      }
    };
    this.featureContext.snapSeg = this.d3svg.append('g');
    this.featureContext.snapPt = this.d3svg.append('g');
    this.featureContext.polygonGroup = this.d3svg.append('g');
    this.featureContext.segmentGroup = this.d3svg.append('g');
    this.featureContext.pointGroup = this.d3svg.append('g');
    this.featureContext.drawRect = this.d3svg.append('g');
    this.featureContext.container = this.d3svg.node();

  }
  SetupInteractionModes() {
    this.interactionModes.feature = new FeatureMode();
    this.interactionModes.rectangle = new RectangleROI();
    this.interactionModes.polygon = new PolygonROI();
  }
  SetupSVGElement() {
    this.d3svg = d3.select(this.svg.nativeElement);
    this.d3svg.attr('fill', '#0000');
    this.SetupSVGGlowFilter();
  }
  SetupSVGGlowFilter() {
    const defs = this.d3svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'glow');

    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3.5')
      .attr('result', 'coloredBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode')
      .attr('in', 'coloredBlur');
    feMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');
  }
  SetupSnaps() {

    if (!(!!this.featureContext && !!this.spatialContext)) {
      window.requestAnimationFrame(() => {
        this.SetupSnaps();
      })
      return;
    }
    this.snapPts = new SnapPts(this.featureContext, this.spatialContext);
    this.snapPts.geometryLoad.subscribe(() => {
      const hadVertex = !this.selectionProxy.GetProperty('snapVertex');
      this.selectionProxy.SetProperty('snapVertex', null);
      this.selectionProxy.SetProperty('snapGeomChange', true);
      if (hadVertex) this.selectionProxy.NotifyChangeListeners();
      this.selectionProxy.SetProperty('snapGeomChange', null);

    })
    this.snapPts.enteredChange.subscribe((pt) => {
      this.selectionProxy.SetProperty('entered', pt);
      this.selectionProxy.SetProperty('entered', null);
    })


    this.snapPts.snapDown.subscribe(() => {
      if (this.interactionMode !== 'feature') return;
      this.map.ui.dragPan.disable();
      this.map.ui.once('mouseup', () => {
        if (this.interactionMode === 'feature') {
          if (this.map.component.IsInteracting('panning')) {
            this.map.ui.dragPan.enable();
          }
        }
      });
    })

    this.snapPts.selectionChange.subscribe((params) => {
      const info = this.snapPts.GetSelectedInfo();
      if (!this.snapGroupMode && !!info?.length) {

        this.snapSegs.Deselect();
      }
      this.selectionProxy.SetProperty('snapVertex', (info.length === 0) ? undefined : info);
    })
    this.snapSegs = new SnapSegs(this.featureContext, this.spatialContext);
    /*this.snapSegs.enteredChange.subscribe((seg) => {
      this.selectionProxy.SetProperty('enteredSeg', seg);
      this.selectionProxy.SetProperty('enteredSeg', null);
    })*/
    this.snapSegs.geometryLoad.subscribe(() => {
      const hadSegment = !this.selectionProxy.GetProperty('snapSegment');
      this.selectionProxy.ToggleUpdating(true);
      this.selectionProxy.SetProperty('snapGeomChange', true);
      this.selectionProxy.SetProperty('snapSegment', null);
      this.selectionProxy.ToggleUpdating(false);
      if (hadSegment) this.selectionProxy.NotifyChangeListeners();
      this.selectionProxy.SetProperty('snapGeomChange', null);

    })

    this.snapSegs.selectionChange.subscribe((params) => {
      const info = this.snapSegs.GetSelectedInfo();
      if (!this.snapGroupMode && !!info?.length) {
        this.snapPts.Deselect();
      }
      this.selectionProxy.SetProperty('snapSegment', (info.length === 0) ? undefined : info)
    })
    if (this.modeStates.GetCurrentState() !== 'snap') {
      this.SnapOff();
    }
  }
  SnapOn(mode: SnapGeomMode = 'any') {
    this.TurnOn();
    this.drawMode = 'snap';
    this.snapGeomMode = mode;
    // this.GetSnaps().points.SetActive(true);
    // this.GetSnaps().segments.SetActive(true);

  }
  SnapOff() {
    if (this.drawMode !== 'snap') return;
    this.drawMode = this.defaultState as DrawMode;
    this.GetSnaps().points.SetActive(false);
    this.GetSnaps().segments.SetActive(false);


  }
  SetSpatialContext(context: PixoSpatial) {
    this.spatialContext = context;
    this.Clear().NewFeature();
  }
  Clear(newOk: boolean = false): DrawPaneComponent {
    for (const feature of this.features) {
      feature.Remove();

    }
    window.removeEventListener('mouseup', this.WindowUpHandler);
    if (newOk === false) {
      this.gid = null;
    }
    this.newFeatureOk = newOk;
    this.features = [];
    return this;
  }
  ClearSnaps() {
    this.GetSnaps().points.Clear();
    this.GetSnaps().segments.Clear();
  }
  NewFeature(keepId = false): DrawPaneComponent {
    if (this.started === false) {
      return;
    }
    this.gid = keepId ? this.gid : null;
    let feature: Feature = null;
    switch (this.geomType) {
      case 'polygon':
        feature = new PolyFeature(this.featureContext, this.spatialContext);
        break;
      case 'line':
        feature = new LineFeature(this.featureContext, this.spatialContext);
        break;
      case 'point':
        feature = new PointFeature(this.featureContext, this.spatialContext);
        break;
    }
    if (!!feature) {
      this.subscriptions.Subscribe(
        feature.selectionChange, (info: Dict) => {
          const selection = this.GetSelectedFeature()?.GetSelectedInfo();
          this.selectionProxy.ToggleUpdating(true);
          if (!selection) {
            this.selectionProxy.SetProperty('segment', null);
            this.selectionProxy.SetProperty('vertex', null);
          }
          switch (selection?.type) {
            case 'segment':
              this.selectionProxy.SetProperty('segment', selection);
              this.selectionProxy.SetProperty('vertex', null);
              break;
            case 'point':
            case 'vertex':
              this.selectionProxy.SetProperty('segment', null);
              this.selectionProxy.SetProperty('vertex', selection);
              break;
            default:
              this.selectionProxy.SetProperty('segment', null);
              this.selectionProxy.SetProperty('vertex', null);
              break;
          }
          this.selectionProxy.ToggleUpdating(false);
          this.selectionChange.emit(info);

        });
      const me = feature;
      this.subscriptions.Subscribe(
        feature.geometryChange, (info: Dict) => {
          if (this.drawMode === 'distance') {
            let length = 0;
            if (me.hasOwnProperty('segments')) {
              (me as any).segments.every((segment) => {
                length += segment.length;
              })
            }
          }
          this.geometryChange.emit(info);
        });


      this.features.push(feature);
    }
    this.selectedFeature = feature;

    this.selectionChange.emit(this);
    return this;
  }
  AddVertex(x, y) {
    const coord = this.spatialContext.PixoSpatialCoord(this.spatialContext.PixoPt({ x: (x), y: (y) }));
    return this.AddVertexFromCoord(coord);

  }

  AddVertexFromCoord(coord: PixoSpatialCoord): any | undefined {
    if (!this.selectedFeature) return;
    if (this.selectedFeature.HasCoord(coord) === true) return undefined
    const info = this.selectedFeature.GetSelectedInfo();
    if (info !== null) {
      if (info.type === 'segment') {
        info.AddVertexFromCoord(coord);
        return this.selectionProxy.GetProperty('vertex');
      }
    }
    return this.selectedFeature.AddVertex(coord);
  }
  DeleteVertex(id?) {
    if (!id && (this.selectedFeature === null)) {
      return;
    }
    if (!this.isOn) return;
    if (this.selectionProxy.GetProperty('vertex').id === this.selectedFeature.GetSelectedId()) {
      this.selectionProxy.SetProperty('vertex', '');
    }
    this.selectedFeature.RemoveSelected();
    this.geometryChange.emit(this.selectedFeature);
  }

  RemoveFeature() {
    if (!this.isOn) return;

    this.features.slice(this.features.indexOf(this.selectedFeature, 1));
    this.selectedFeature.Remove();
    this.selectedFeature = null;
    if (this.features.length > 0) {
      this.SelectFeature(this.features[this.features.length - 1]);
    }
    this.selectionProxy.ToggleUpdating(false);
    this.selectionProxy.SetProperty('vertex', undefined);
    this.selectionProxy.SetProperty('segment', undefined);
    this.selectionChange.emit(this.GetSelectedFeature());
  }
  SelectFeature(feature) {
    if (this.selectedFeature !== null) {
      if (this.selectedFeature.id === feature.id) {
        return;
      }
      this.selectedFeature.DeselectFeature();
    }
    this.selectedFeature = feature;
    if (feature === null) { return; }
    this.selectedFeature.SelectFeature();
  }
  RemoveFeatureSelection() {
    this.selectedFeature.RemoveSelected();
  }

  ngOnDestroy(): void {
    this.subscriptions.CancelSubscriptions();
    for (const g in this.featureContext) {
      if (this.featureContext.hasOwnProperty(g)) {
        this.featureContext[g].destroy();
      }
    }
    if (this.spatialContext !== null) {
      this.spatialContext.Detatch();
    }
  }
  TurnOn() {
    if (this.stateMgr.GetCurrentState() === 'on') return;
    this.stateMgr.SetState('on');
    this.domNode.nativeElement.classList.toggle('hidden', false);
    // this.drawMode = 'feature';

    this.domNode.nativeElement.classList.toggle('hidden', false);
    if (this.layer && this.layer.SLMap) {
      // this.layer.SLMap.DigitizerUpdated();
    }
    if (this.isOn === true) {
      if (this.domNode.nativeElement.classList.contains('active')) return;
    }
    this.isOn = true;
    this.domNode.nativeElement.classList.toggle('active', this.isOn);
    DisplayAction.UpdateDrawListeners(this.map.component, {drawpane:this,  is_on: this.isOn });

    this.isOnChange.emit(true);


  }
  TurnOff(hide = false) {
    this.domNode.nativeElement.classList.toggle('hidden', hide);
    DisplayAction.UpdateDrawListeners(this.map.component, {drawpane:this,  is_on: false});
    if (this.stateMgr.GetCurrentState() === 'off') return;
    this.stateMgr.SetState('off');
    this.domNode.nativeElement.classList.toggle('hidden', hide);
    this.domNode.nativeElement.classList.toggle('active', this.isOn);
    if (this.layer && this.layer.SLMap) {
      this.layer.SLMap.DigitizerUpdated();
    }

    if (this.isOn === false) {
      return;
    }

    this.isOn = false;


    this.isOnChange.emit(false);


  }
  SetLayer(layer: SLLayer | '') {
    if (this.layer && layer && (layer.id === this.layer.id)) return;
    if (layer === '') (this.layer as any) = undefined
    else {
      this.layer = layer;
    }
    if (!this.layer) {
      this.Clear(false);
      this.type = null;
      return;
    }
    if (this.layer) {
      this.type = this.layer.geom;
    }
  }
  GPSUpdateSelected() {
    const info = this.selectedFeature?.GetSelectedInfo();
    if (info === null) {
      return;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        (info.coord as PixoSpatialCoord).UpdateSpatial(position.coords.longitude, position.coords.latitude,
          position.coords.accuracy, position.timestamp, position.coords.altitude, position.coords.altitudeAccuracy,
          position.coords.heading, position.coords.speed);
        this.geometryChange.emit(this.selectedFeature);
      });
    } else {
      this.messager.Notify('Geolocation is not supported by this browser');

    }
  }
  GPSAddVertex() {
    const me = this;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        me.AddFromGPS(position);
      });

    } else {
      this.messager.Notify('Geolocation is not supported by this browser');

    }
  }
  AddFromGPS(position) {
    if (!this.selectedFeature) return;
    const spatial = this.spatialContext.SpatialCoord(position.coords, 'longitude', 'latitude');
    const pixo = spatial.convert();


    this.AddVertex(pixo.x, pixo.y);
    const info = this.selectedFeature.GetSelectedInfo();
    if (info.type === 'point') {
      info.coord.UpdateSpatial(position.coords.longitude, position.coords.latitude,
        position.coords.accuracy, position.timestamp, position.coords.altitude, position.coords.altitudeAccuracy,
        position.coords.heading, position.coords.speed);
      this.geometryChange.emit(this.selectedFeature);
    }
    const slippy = (this.map.ui as mapboxgl.Map);
    const bounds: LngLatBounds = slippy.getBounds();
    if (!bounds.contains(spatial)) {
      this.messager.Confirm('The point you added via GPS is outside the current map area. Select an option below to continue.', (params) => {
        switch (params.option.name) {
          case 'stay_here':
            break;
          case 'center':
            slippy.jumpTo({ center: spatial })
            break;
          case 'fit':
            EditorAction.JumpToGeometry(this.map.component);
            break;
        }

      }, (e) => {
        console.log(e);
      }, 'Point outside map', [
        { 'label': 'Stay at current location', name: 'stay_here' },
        { 'label': 'Center map on new point', name: 'center' },
        { 'label': 'Fit current geometry to view', name: 'fit' }
      ])
    }
  }
  UpdateFromGPS(position, accurate: boolean = false) {
    const spatial = this.spatialContext.SpatialCoord(position.coords, 'longitude', 'latitude');
    const pixo = spatial.convert();
    const info = this.selectedFeature.GetSelectedInfo();
    if (info.type === 'point') {
      info.coord.UpdateSpatial(position.coords.longitude, position.coords.latitude,
        position.coords.accuracy, position.timestamp, position.coords.altitude, position.coords.altitudeAccuracy,
        position.coords.heading, position.coords.speed, accurate);
      this.geometryChange.emit(this.selectedFeature);
    }
  }
  UpdateAll() {
    for (const feature of this.features) {
      feature.ContextChanged();
    }
  }
  private _geomPromise: CancellablePromise;

  ShowFeature(featureInfo: FeatureInfo, readyHandler: AnyHandler, force: boolean = false) {

    this.layer = featureInfo.layer;
    if (!this.layer) return;
    if (this.layer.SLMap.digitizer) {
      this.layer.SLMap.digitizer.SetDrawPane(this);
    }
    // if(this.gid === ''+featureInfo.gid) return;
    this._geomPromise?.abort();
    this._geomPromise = this.featureSvc.GetGeoJSON(featureInfo.layer, featureInfo.gid);
    this._geomPromise.then((response) => {
      this.TurnOn();
      this.SetLayer(featureInfo.layer);
      this.drawMode = 'feature';
      this.type = featureInfo.layer.geom;
      this.Clear().NewFeature(true);
      this.gid = '' + featureInfo.gid;
      if (!!this.features[0].FromGeoJSONGeom) {
        this.features[0].FromGeoJSONGeom(response.record.json_geom);
        this.layer.SLMap.digitizer.SetDrawPaneFeatureReady(this.features[0]);
        readyHandler();
      }
    });
  }
  SaveFeature(returnToInfo: boolean = true, gid?: any) {
    const geoGeom = this.selectedFeature.ToGeoJSONGeom();
    if (geoGeom === false) {
      this.messager.Notify('You are attempting to save geometry with too few vertices', true, 'Insufficient Vertices');
      return;
    }
    if (this.hasVertices === false) return;
    this.featureSvc.SaveGeoJSON(
      this.layer,
      this.selectedFeature.ToGeoJSONGeom() as GeometryObject,
      (!!gid) ? gid : this.gid).then((response) => {
        this.Cancel();
        if (returnToInfo) {
          this.layer.map.component.HandleToolAction(
            {
              action: 'info/get-added-feature',
              layer: this.layer,
              feature: response.record
            }
          );
        }
      });
  }
  Cancel() {
    this.Clear().NewFeature();
    this.layer?.SLMap?.Update(this.layer);
  }

  EnableDistance(geom: 'point' | 'line' | 'polygon') {
    this.Clear(true);
    this.isDistanceOn = true;
    this.drawMode = 'distance';
    this.geomType = geom;
  }
  DisableDistance() {
    this.isDistanceOn = false;
  }

  UpdateSize(width, height) {
    this.domNode.nativeElement.style.width = width;
    this.domNode.nativeElement.style.height = height;
    this.drawWidth = width;
    this.drawHeight = height;
    if (!!this.featureContext?.drawRect) {
      this.featureContext.drawRect.attr('width', width);
      this.featureContext.drawRect.attr('height', height);
    }
  }
  GetSelectedFeature(): Feature {
    return this.selectedFeature as Feature;
  }
  GetSnaps() {
    return { points: this.snapPts, segments: this.snapSegs }
  }
  DeselectSnaps() {
    this.GetSnaps().points.Deselect();
    this.GetSnaps().segments.Deselect();
  }
  UnsetDrawMode(mode: DrawMode) {
    if (this._drawMode === mode) {
      this.drawMode = '';
      this.SetInteractionMode('');
    }
  }
  UnsetIteractionMode(mode: DrawInteractionModes) {
    if (this._xactionMode === mode) {
      this.SetInteractionMode('');
    }
  }
  SetInteractionMode(mode: DrawInteractionModes, altHandler?: SimpleAVHandler<any>, params?: any) {
    if (!this.featureContext?.drawRect) return;
    if (this._xactionMode === mode) {
      if (this.interactionModes[this._xactionMode]?.enabled) return;
    }
    if (this._xactionMode !== '') {
      this.interactionModes[this._xactionMode]?.Disable();
    }
    this.interactionModes[mode]?.Enable(this, this.featureContext.drawRect, (data: any) => {
      switch (mode) {
        case 'feature':
          this.HandleFeatureInteraction(data as PixoSpatialCoord);
          break;
        case 'rectangle':
          this.HandleROI(data as { from: PixoSpatialCoord, to: PixoSpatialCoord }, altHandler as SimpleAVHandler<ROICorners>);
          break;
        case 'polygon':
          this.HandlePolygonROI(data as GeoJSONObject | undefined, altHandler as SimpleAVHandler<GeoJSONObject>);
      }
    }, params);
    this._xactionMode = mode;

  }
  ConfirmNewKnowledge(handler: SimpleHandler) {
    if (this.newFeatureOk === true) {
      handler();
      return;
    }
    this.messager.Confirm(
      'A feature is not currently being edited, if you are intentionally creating a new feature? Click OK to proceed editing your new feature.',
      () => {
        this.newFeatureOk = true;
        handler();
      }, () => {

      }
    )
  }
  HandleFeatureInteraction(coord: PixoSpatialCoord, handler?: SimpleAVHandler<any>, force = false) {
    if (!this.isOn && !force) return;
    if (this.gid === null) {
      if (this.newFeatureOk === false) {
        this.messager.Confirm(
          'A feature is not currently being edited, if you are intentionally creating a new feature? Click OK to proceed editing your new feature.',
          () => {
            this.newFeatureOk = true;
            const added = this.AddVertexFromCoord(coord);
            if (handler) handler(added);
          }, () => { }
        );
        return;
      }
    }
    this.AddVertexFromCoord(coord);
  };
  HandleROI(corners: ROICorners, altHandler?: SimpleAVHandler<ROICorners>) {
    if (altHandler) {
      altHandler(corners);
      return;
    }
    const mode = this.map.component.HandleROI(corners);
    // this.SetInteractionMode(mode as DrawInteractionModes);
  }
  HandlePolygonROI(roi?: GeoJSONObject, altHandler?: SimpleAVHandler<GeoJSONObject>) {
    if (altHandler) {
      altHandler(roi);
      return;
    }
    if (roi) {
      switch (this._drawMode) {
        case 'roi-polygon':
          // Tell the map component about it
          break;
        case 'roi-snap':
        // Notify the snap tech
      }
    }
    this.SetInteractionMode('');
  }
  GetROI(endMode: DrawInteractionModes) {
    this.selectionProxy.SetProperty('ROICorners', undefined);
    this.SnapOff();
    this.SetInteractionMode('rectangle', (corners: ROICorners) => {
      this.selectionProxy.SetProperty('ROICorners', corners);

      this.selectionProxy.SetProperty('ROICorners', undefined);

    }, { autoEnd: false });

  }
}
